import * as Sentry from '@sentry/nextjs';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
} from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAnalytics } from '@features/Analytics';
import {
  stripePaymentErrorAtom,
  stripeTryAnotherCardAtom,
} from '@features/Stripe/atoms';
import {
  CARD_ELEMENT_OPTIONS,
  ELEMENT_OPTIONS,
} from '@features/Stripe/constants';
import {
  useStripePaymentRetryAnalytics,
  useStripeTryAnotherCard,
} from '@features/Stripe/hooks';
import { schedulePaymentRecovery } from '@features/Stripe/utils/schedule-payment-recovery';
import {
  getStripeRetryablePaymentErrorCode,
  isStripeTryAnotherCardPaymentError,
} from '@features/Stripe/utils/stripe-payment-errors';
import { DebitCreditBlock } from '@features/Stripe/widgets/checkout-form/widgets/debit-credit-block';
import { PaymentRetryDeclinedModal } from '@features/Stripe/widgets/payment-retry-declined-modal';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { UiKitLoadingButton } from 'src/components/common/ui-kit-loading-button';
import { CardholderName } from '../card-holder-name';

import styles from './card-payment-block.module.scss';

type Props = {
  stripe: any;
  clientSecret: string;
  planId: string;
  minimalStyle?: boolean;
  userEmail: string;
  handleAddPaymentInfoEventsSend: ({
    paymentMethod,
  }: {
    paymentMethod: string;
  }) => void;
  handleProcessingStatus: (status: boolean) => void;
  handleSuccessPayment: ({
    paymentMethod,
  }: {
    paymentMethod: string;
  }) => Promise<void>;
};

export const CardPaymentBlock = observer(
  ({
    stripe,
    clientSecret,
    planId,
    userEmail,
    handleAddPaymentInfoEventsSend,
    handleSuccessPayment,
    handleProcessingStatus,
    minimalStyle,
  }: Props) => {
    const elements = useElements();
    const {
      resetRetryPaymentErrorEscalation,
      shouldEscalateRetryPaymentError,
      trackRetryPaymentShown,
      trackRetryPaymentTapped,
    } = useStripePaymentRetryAnalytics();
    const { showTryAnotherCardModal } = useStripeTryAnotherCard();
    const { clearCardFormCount } = useAtomValue(stripeTryAnotherCardAtom);
    const previousClearCardFormCount = useRef(clearCardFormCount);
    const isRetryPaymentAttemptRef = useRef(false);

    const [message, setMessage] = useState<string | ReactNode>('');
    const [isLoading, setIsLoading] = useState(true);
    const [name, setName] = useState('');
    const [nameIsValid, setNameIsValid] = useState(false);
    const [zipCode, setZipCode] = useState('');
    const [showRetryDeclinedModal, setShowRetryDeclinedModal] = useState(false);
    const {
      quizStore: { countryCode: countryFromStorage },
    } = useStores();

    const isCountryUS = countryFromStorage === 'US';

    const [userStartEnteringCardInfo, setUserStartEnteringCardInfo] =
      useState(false);

    const [, setPaymentError] = useAtom(stripePaymentErrorAtom);

    const { trackGoogleEvent } = useAnalytics();

    const handleUserStartEnteringCardInfo = useCallback(
      () => setUserStartEnteringCardInfo(true),
      [],
    );

    const handleNameUpdate = useCallback((newName: string) => {
      setName(newName);
    }, []);

    const handleValidationUpdate = useCallback(
      (isValid: boolean, validationMessage: string) => {
        setMessage(validationMessage);
        setNameIsValid(isValid);
      },
      [],
    );

    const checkIsPrepaidCard = useCallback(async () => {
      if (!stripe || !elements) {
        return;
      }

      const card = elements.getElement(CardNumberElement);

      try {
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            email: userEmail,
            name: minimalStyle ? undefined : name,
          },
        });

        if (paymentMethod?.card.funding === 'prepaid') {
          setMessage(
            'Prepaid cards are not supported. Please use a different payment method.',
          );
          return 'prepaid';
        }

        return paymentMethod;
      } catch (error) {
        console.error('Error creating payment method:', error);
        throw error;
      }
    }, [elements, name, stripe, userEmail, minimalStyle]);

    const handleZipCodeEvent = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setZipCode(event.target.value);
        event.target.value.length > 0 && handleUserStartEnteringCardInfo();
      },
      [handleUserStartEnteringCardInfo],
    );

    const memoizedZipCodeValidation = useMemo(() => {
      if (zipCode.length === 0) {
        setMessage('');
        return false;
      }

      if (zipCode.length > 0) {
        setMessage('');
        return true;
      } else {
        setMessage('Please enter a valid zip code');
        return false;
      }
    }, [zipCode]);

    useEffect(() => {
      if (userStartEnteringCardInfo) {
        handleAddPaymentInfoEventsSend({ paymentMethod: 'card' });
      }
    }, [handleAddPaymentInfoEventsSend, userStartEnteringCardInfo]);

    useEffect(() => {
      if (elements) {
        const element = elements.getElement(CardNumberElement);
        element !== null &&
          element.on('ready', () => {
            setIsLoading(false);
          });
      }
    }, [elements]);

    useEffect(() => {
      if (clearCardFormCount === previousClearCardFormCount.current) {
        return;
      }

      if (!elements) {
        return;
      }

      elements.getElement(CardNumberElement)?.clear();
      elements.getElement(CardExpiryElement)?.clear();
      elements.getElement(CardCvcElement)?.clear();
      setName('');
      setNameIsValid(false);
      setMessage('');
      previousClearCardFormCount.current = clearCardFormCount;
    }, [clearCardFormCount, elements]);

    const handleSubmit = useCallback(
      async (event: any) => {
        event.preventDefault();
        const wasRetryPaymentAttempt = isRetryPaymentAttemptRef.current;
        isRetryPaymentAttemptRef.current = false;
        trackGoogleEvent({
          eventName: 'add_payment_info_chosen',
          options: {
            email: userEmail,
          },
        });
        setIsLoading(true);
        handleProcessingStatus(true);

        if (!stripe || !elements) {
          setIsLoading(false);
          handleProcessingStatus(false);
          return;
        }

        if ((await checkIsPrepaidCard()) === 'prepaid') {
          setIsLoading(false);
          handleProcessingStatus(false);
          return;
        }

        // Get a reference to a mounted CardElement. Elements knows how
        // to find your CardElement because there can only ever be one of
        // each type of element.
        const card = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        if (card == null) {
          setIsLoading(false);
          handleProcessingStatus(false);
          return;
        }

        // Use card Element to tokenize payment details
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: {
              card: card,
              billing_details: {
                name: minimalStyle ? undefined : name,
                email: userEmail,
                address: {
                  postal_code: zipCode,
                },
              },
            },
          },
        );

        card?.blur();
        cardExpiry?.blur();
        cardCvc?.blur();

        if (error) {
          const shouldShowTryAnotherCardModalByError =
            isStripeTryAnotherCardPaymentError({
              error,
              paymentIntent: error.payment_intent,
            });
          const retryablePaymentErrorCode = getStripeRetryablePaymentErrorCode({
            error,
            paymentIntent: error.payment_intent,
          });
          const shouldEscalateRetryErrorToTryAnotherCardModal =
            !shouldShowTryAnotherCardModalByError && retryablePaymentErrorCode
              ? shouldEscalateRetryPaymentError({
                  errorCode: retryablePaymentErrorCode,
                  isRetryPaymentAttempt: wasRetryPaymentAttempt,
                  paymentMethod: 'card',
                  planId,
                })
              : false;
          const shouldShowTryAnotherCardModal =
            shouldShowTryAnotherCardModalByError ||
            shouldEscalateRetryErrorToTryAnotherCardModal;
          const shouldShowRetryDeclinedModal =
            !!retryablePaymentErrorCode && !shouldShowTryAnotherCardModal;

          Sentry.captureException(error);
          trackGoogleEvent({
            eventName: 'payment_error',
            options: {
              errorCode: error?.code,
              errorDeclineCode: error?.decline_code,
              paymentMethod: 'card',
              paymentSystem: 'Stripe',
            },
          });
          setMessage(error.message as string);
          setIsLoading(false);
          handleProcessingStatus(false);

          const showPaymentRecovery = () => {
            if (shouldShowTryAnotherCardModal) {
              showTryAnotherCardModal({
                paymentMethod: 'card',
                planId,
              });
            } else if (shouldShowRetryDeclinedModal) {
              trackRetryPaymentShown({
                paymentMethod: 'card',
                planId,
              });
            }

            setShowRetryDeclinedModal(shouldShowRetryDeclinedModal);
            setPaymentError((previous) => ({
              ...previous,
              declineCode: error.decline_code ?? '',
              errorCode: error.code ?? '',
              paymentMethod: 'card',
              paymentSystem: 'Stripe',
            }));
          };

          schedulePaymentRecovery(showPaymentRecovery);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          resetRetryPaymentErrorEscalation();
          await handleSuccessPayment({ paymentMethod: 'card' });
        } else {
          resetRetryPaymentErrorEscalation();
          setMessage('Unexpected state');
          setIsLoading(false);
          handleProcessingStatus(false);
        }
      },
      [
        minimalStyle,
        checkIsPrepaidCard,
        clientSecret,
        elements,
        handleProcessingStatus,
        handleSuccessPayment,
        name,
        planId,
        resetRetryPaymentErrorEscalation,
        setPaymentError,
        shouldEscalateRetryPaymentError,
        showTryAnotherCardModal,
        stripe,
        trackGoogleEvent,
        trackRetryPaymentShown,
        userEmail,
        zipCode,
      ],
    );

    const handleCloseRetryDeclinedModal = useCallback(() => {
      setShowRetryDeclinedModal(false);
    }, []);

    const handleRetryPayment = useCallback(() => {
      trackRetryPaymentTapped({
        paymentMethod: 'card',
        planId,
      });
      setShowRetryDeclinedModal(false);
      isRetryPaymentAttemptRef.current = true;
      void handleSubmit({ preventDefault: () => undefined });
    }, [handleSubmit, planId, trackRetryPaymentTapped]);

    const renderCardHolderName = useCallback(
      () => (
        <CardholderName
          name={name}
          onNameUpdate={handleNameUpdate}
          onValidationUpdate={handleValidationUpdate}
        />
      ),
      [handleNameUpdate, name, handleValidationUpdate],
    );

    return (
      <div
        className={classNames(styles.cardBlockContainer, {
          [styles.minimal]: minimalStyle,
        })}
      >
        <DebitCreditBlock />

        <div className={styles.cardContainer}>
          <div className={styles.cardElement}>
            <CardNumberElement
              onChange={(event) =>
                !event.empty && handleUserStartEnteringCardInfo()
              }
              id="cardNumber"
              options={CARD_ELEMENT_OPTIONS}
            />
          </div>

          {renderCardHolderName()}

          <div className={styles.expiredAndCvc}>
            <div className={styles.expiredElement}>
              <CardExpiryElement
                id="expiry"
                options={ELEMENT_OPTIONS}
                onChange={(event) =>
                  !event.empty && handleUserStartEnteringCardInfo()
                }
              />
            </div>
            <div className={styles.cvcElement}>
              <CardCvcElement
                id="cvc"
                onChange={(event) =>
                  !event.empty && handleUserStartEnteringCardInfo()
                }
                options={ELEMENT_OPTIONS}
              />
            </div>
          </div>
        </div>

        {isCountryUS && (
          <>
            <h4 className={styles.zipTitle}>ZIP/Postal Code</h4>
            <div className={styles.zipCodeElement}>
              <input
                className={styles.input}
                id="zipCode"
                required
                type="text"
                placeholder="XXXX XXXX"
                value={zipCode}
                onChange={handleZipCodeEvent}
              />
            </div>
          </>
        )}

        {message && <span className={styles.message}>{message}</span>}
        <UiKitLoadingButton
          className={styles.proceed}
          data-testid="stripe-submit-button"
          disabled={
            isLoading ||
            !stripe ||
            !nameIsValid ||
            (isCountryUS && !memoizedZipCodeValidation)
          }
          id="submit"
          onClick={handleSubmit}
          type="submit"
          loading={isLoading}
        >
          Continue with card
        </UiKitLoadingButton>
        <PaymentRetryDeclinedModal
          isLoading={isLoading}
          onClose={handleCloseRetryDeclinedModal}
          onRetry={handleRetryPayment}
          show={showRetryDeclinedModal}
        />
      </div>
    );
  },
);

CardPaymentBlock.displayName = 'CardPaymentBlock';
